/**
 * Layout-only page: shows the trading layout (ShowcaseLayout) without any
 * configuration UI. All settings are hardcoded below for easy editing.
 */
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
} from "react";
import { toast } from "sonner";
import type { ResponsiveLayouts, Layout } from "react-grid-layout";
import {
  ShowcaseLayout,
  type LayoutItem,
  TradingViewWidget,
  Orderbook002,
  TradePrice,
  TradeOperation,
  PendingOrders,
  BucketPositions,
  BucketCollections,
  LeverageModal,
  NumberPad,
  generateLayout,
  TickerBar,
  TabSection,
} from "../components";
import { useMarkets } from "../hooks/useMarkets";
import { useOnboard } from "../hooks/useOnboard";
import { DEFAULT_JITTER } from "../utils/orderbookEnhancement";
import { DEFAULT_MNEMONIC } from "../components/smartlayout/constants";
import { demoDesignatedLayouts } from "../configs/layoutConfig";
import {
  SubscriptionProvider,
  useSubscription,
} from "../context/SubscriptionContext";
import { capitalize } from "../components/smartlayout/util";

// =============================================================================
// HARDCODED CONFIGURATION – edit these to change behavior
// =============================================================================
const CONFIG = {
  baseURL: "https://router.morpheum.io",
  wsURL: "wss://router.morpheum.io/websocket",
  layoutConfig: demoDesignatedLayouts,
  compactType: "vertical" as const,
  allStatic: true,
  fullWidth: false,
  orderbookEnhancement: {
    enabled: true,
    jitter: DEFAULT_JITTER,
    fakePending: {
      maxFakeLevelsPerSide: 3,
      minLifetimeMs: 2000,
      maxLifetimeMs: 8000,
      volumeScalePct: 0.8,
      minAddDelayMs: 1500,
      maxAddDelayMs: 5000,
    },
  },
  orderbookMaxDepth: 20,
  refreshInterval: 5000,
  tradingViewSymbol: "BINANCE:BTCUSDT",
  tradingViewInterval: "5",
} as const;

// =============================================================================
// LAYOUT GRID CONSTANTS
// =============================================================================
const ROW_HEIGHT = 30;
const MARGIN_Y = 10;
const COLS = 12;
/** Column counts per breakpoint (must match ShowcaseLayout default / props) */
const CURRENT_COLS: Record<string, number> = {
  lg: 12,
  md: 10,
  sm: 6,
  xs: 4,
  xxs: 2,
};

function LayoutOnlyContent() {
  const [selectedBucketId, setSelectedBucketId] = useState<string>("--");
  const [selectedBucketBalance, setSelectedBucketBalance] = useState<
    number | undefined
  >(undefined);
  const [isLeverageModalOpen, setIsLeverageModalOpen] = useState(false);
  const [tempLeverage, setTempLeverage] = useState(10);
  const [isNumberCalculatorOpen, setIsNumberCalculatorOpen] = useState(false);
  const [numberCalculatorValue, setNumberCalculatorValue] = useState(0);
  const [numberCalculatorMax, setNumberCalculatorMax] = useState(0);
  const showcaseLayoutRef = useRef<ShowcaseLayout>(null);
  const tradeOperationRef =
    useRef<import("../components/TradeOperation").TradeOperationRef>(null);

  const [currentBreakpoint, setCurrentBreakpoint] = useState<string>("lg");
  const [isLayoutMounted, setIsLayoutMounted] = useState(false);
  const hasTriggeredToggleRef = useRef(false);
  const prevLayoutRef = useRef<LayoutItem[]>([]);

  const initialLayout = useMemo(
    () => generateLayout(CONFIG.layoutConfig, 0),
    [],
  );

  const [layout, setLayout] = useState<LayoutItem[]>(() =>
    generateLayout(CONFIG.layoutConfig, 0),
  );

  useEffect(() => {
    setLayout(initialLayout);
    prevLayoutRef.current = initialLayout;
    setIsLayoutMounted(false);
    hasTriggeredToggleRef.current = false;
  }, [initialLayout]);

  const [sessionToken, setSessionToken] = useState(() =>
    localStorage.getItem("authToken") || "",
  );

  const {
    markets,
    selectedMarketId,
    loadingMarkets,
    getError,
  } = useMarkets({
    baseURL: CONFIG.baseURL,
    sessionToken,
    autoFetchMarkets: true,
  });

  const { toggleEnabled } = useSubscription();
  const {
    mnemonic,
    walletAddress,
    authToken: onboardAuthToken,
    setMnemonic: setOnboardMnemonic,
  } = useOnboard(CONFIG.baseURL);

  useEffect(() => {
    if (!mnemonic) {
      setOnboardMnemonic(DEFAULT_MNEMONIC);
    }
  }, [mnemonic, setOnboardMnemonic]);

  useEffect(() => {
    prevLayoutRef.current = layout;
  }, [layout]);

  useEffect(() => {
    if (onboardAuthToken) {
      setSessionToken(onboardAuthToken);
      localStorage.setItem("authToken", onboardAuthToken);
    }
  }, [onboardAuthToken]);

  const { setAuthToken, setWalletAddress } = useSubscription();
  useEffect(() => {
    setAuthToken(sessionToken || undefined);
  }, [sessionToken, setAuthToken]);
  useEffect(() => {
    setWalletAddress(walletAddress || undefined);
  }, [walletAddress, setWalletAddress]);

  const isSyncingFromStorageRef = useRef(false);
  useEffect(() => {
    if (
      sessionToken &&
      sessionToken.trim() !== "" &&
      !isSyncingFromStorageRef.current
    ) {
      const stored = localStorage.getItem("authToken");
      if (stored !== sessionToken) {
        localStorage.setItem("authToken", sessionToken);
      }
    }
    isSyncingFromStorageRef.current = false;
  }, [sessionToken]);

  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "authToken" && e.newValue) {
        isSyncingFromStorageRef.current = true;
        setSessionToken(e.newValue);
      }
    };
    window.addEventListener("storage", handleStorageChange);
    const interval = setInterval(() => {
      const storedAuthToken = localStorage.getItem("authToken");
      setSessionToken((currentToken) => {
        if (storedAuthToken && storedAuthToken !== currentToken) {
          isSyncingFromStorageRef.current = true;
          return storedAuthToken;
        }
        return currentToken;
      });
    }, 5000);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
      clearInterval(interval);
    };
  }, []);

  const handleLayoutMount = useCallback(() => {
    setIsLayoutMounted(true);
  }, []);

  useEffect(() => {
    const isReady =
      isLayoutMounted &&
      !loadingMarkets &&
      markets.length > 0 &&
      selectedMarketId &&
      !hasTriggeredToggleRef.current;

    if (isReady) {
      hasTriggeredToggleRef.current = true;
      setTimeout(() => {
        toggleEnabled();
      }, 100);
    }
  }, [
    isLayoutMounted,
    loadingMarkets,
    markets.length,
    selectedMarketId,
    toggleEnabled,
  ]);

  const onLayoutChange = useCallback(
    (newLayout: Layout[], _layouts: ResponsiveLayouts) => {
      if (CONFIG.allStatic) return;
      const prevLayout = prevLayoutRef.current;
      const newLayoutItems = newLayout as LayoutItem[];
      if (prevLayout.length !== newLayoutItems.length) {
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
        return;
      }
      let hasChanged = false;
      for (let i = 0; i < prevLayout.length; i++) {
        const prev = prevLayout[i] as unknown as { i: string; x: number; y: number; w: number; h: number };
        const next = newLayoutItems[i] as unknown as { i: string; x: number; y: number; w: number; h: number };
        if (
          prev.x !== next.x ||
          prev.y !== next.y ||
          prev.w !== next.w ||
          prev.h !== next.h ||
          prev.i !== next.i
        ) {
          hasChanged = true;
          break;
        }
      }
      if (hasChanged) {
        prevLayoutRef.current = newLayoutItems;
        setLayout(newLayoutItems);
      }
    },
    [],
  );

  useEffect(() => {
    if (CONFIG.fullWidth) {
      document.body.classList.add("full-width-screen");
    } else {
      document.body.classList.remove("full-width-screen");
    }
    return () => document.body.classList.remove("full-width-screen");
  }, []);

  const currentMarketId: string = selectedMarketId || "";

  const slotDimensions = useMemo(() => {
    if (layout.length === 0) return {};
    const dimensions: {
      [slotIndex: number]: { width: string; height: string };
    } = {};
    for (let slotIndex = 0; slotIndex <= 8; slotIndex++) {
      const layoutItem = layout.find((item) => {
        const itemLayout = item as unknown as { i: string };
        return itemLayout.i === slotIndex.toString();
      });
      if (layoutItem) {
        const itemLayout = layoutItem as unknown as { w: number; h: number };
        const pixelHeight =
          itemLayout.h * ROW_HEIGHT + (itemLayout.h - 1) * MARGIN_Y;
        const widthPercent = (itemLayout.w / COLS) * 100;
        dimensions[slotIndex] = {
          width: `${widthPercent}%`,
          height: `${pixelHeight}px`,
        };
      }
    }
    return dimensions;
  }, [layout]);

  const handleCopyLayoutMatrix = useCallback(async () => {
    try {
      // Sort layout items by slot index (i) and format as [x, y, w, h] arrays
      const layoutMatrix = layout
        .slice()
        .sort((a, b) => {
          const aLayout = a as any;
          const bLayout = b as any;
          const aIndex = parseInt(aLayout.i, 10);
          const bIndex = parseInt(bLayout.i, 10);
          return aIndex - bIndex;
        })
        .map((item) => {
          const itemLayout = item as any;
          return `  [${itemLayout.x}, ${itemLayout.y}, ${itemLayout.w}, ${itemLayout.h}]`;
        })
        .join(",\n");

      const formattedOutput = `[\n${layoutMatrix},\n]`;
      await navigator.clipboard.writeText(formattedOutput);
      alert("Layout matrix copied to clipboard!");
    } catch (err) {
      console.error("Failed to copy layout matrix:", err);
      alert("Failed to copy layout matrix to clipboard");
    }
  }, [layout]);


  // Memoize stringifyLayout to prevent recalculation on every render
  const stringifyLayout = useMemo(() => {
    return layout.map(function (l) {
      const layoutItem = l as any;
      return (
        <div className="layoutItem" key={layoutItem.i}>
          <b>{layoutItem.i}</b>: [{layoutItem.x}, {layoutItem.y}, {layoutItem.w}
          , {layoutItem.h}]
        </div>
      );
    });
  }, [layout]);

  const handleOrderbookPriceClick = useCallback((price: number) => {
    if (tradeOperationRef.current) {
      tradeOperationRef.current.setPrice(price);
    }
  }, []);

  const { client: subscriptionClient } = useSubscription();

  const demoComponents: React.ReactNode[] = useMemo(
    () => [
      <TickerBar
      key={`ticker-${currentMarketId}`}
      baseURL={CONFIG.baseURL}
      selectedMarketId={currentMarketId}
      height={slotDimensions[0]?.height}
    />,
      <TradingViewWidget
        key="tv"
        symbol={CONFIG.tradingViewSymbol}
        interval={CONFIG.tradingViewInterval}
        isCollapsed={false}
      />,
      <Orderbook002
        key={`clobx-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        subscriptionClient={subscriptionClient}
        marketId={currentMarketId}
        maxDepth={CONFIG.orderbookMaxDepth}
        height={slotDimensions[2]?.height}
        onPriceClick={handleOrderbookPriceClick}
        enhancementConfig={CONFIG.orderbookEnhancement}
      />,
      <TradePrice
        key={`price-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        subscriptionClient={subscriptionClient}
        marketId={currentMarketId}
      />,
      <TradeOperation
        ref={tradeOperationRef}
        key={`trade-${currentMarketId}`}
        baseURL={CONFIG.baseURL}
        mnemonic={mnemonic ?? DEFAULT_MNEMONIC}
        marketId={currentMarketId}
        selectedBucketBalance={selectedBucketBalance}
        selectedBucketId={selectedBucketId}
        authToken={sessionToken}
        walletAddress={walletAddress ?? ""}
        onLeverageClick={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(true);
        }}
        onNumberCalculatorClick={() => {
          if (tradeOperationRef.current) {
            setNumberCalculatorValue(tradeOperationRef.current.getSizeInput());
            setNumberCalculatorMax(
              tradeOperationRef.current.getAvailableTradeAmount(),
            );
            setIsNumberCalculatorOpen(true);
          }
        }}
      />,
      <TabSection
      baseURL={CONFIG.baseURL}
      subscriptionClient={subscriptionClient}
      authToken={sessionToken}
      walletAddress={walletAddress}
      mnemonic={mnemonic}
      height="400px"
      onBucketSelect={(bucketId: string) => setSelectedBucketId(bucketId)}
      selectedBucketId={selectedBucketId}
      />
    ],
    [
      currentMarketId,
      slotDimensions,
      selectedBucketId,
      selectedBucketBalance,
      handleOrderbookPriceClick,
      subscriptionClient,
      mnemonic,
      sessionToken,
      walletAddress,
    ],
  );

  if (loadingMarkets) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>Loading markets...</div>
      </div>
    );
  }

  const marketsError = getError("markets");
  if (marketsError) {
    return (
      <div style={{ padding: "2rem", textAlign: "center", color: "#ff6b6b" }}>
        <div>Error loading markets: {marketsError}</div>
      </div>
    );
  }

  if (markets.length === 0 || !selectedMarketId) {
    return (
      <div style={{ padding: "2rem", textAlign: "center" }}>
        <div>No markets available.</div>
      </div>
    );
  }

  return (
    <div className="App-content-row">

{/* Layout Matrix Display – only when layout is editable (not all static) */}
      {!CONFIG.allStatic && (
        <div className="overview-layout layoutJSON">
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "0.5rem",
            }}
          >
            <div>
              Current Breakpoint (from ShowcaseLayout): {currentBreakpoint} (
              {CURRENT_COLS[currentBreakpoint] ?? "—"} columns)
            </div>
            <div>Compaction type: {capitalize(CONFIG.compactType ?? "") || "No Compaction"}</div>
            <span>
              Displayed as <code>[x, y, w, h]</code>
            </span>
            <button
              onClick={handleCopyLayoutMatrix}
              style={{
                padding: "0.5rem 1rem",
                fontSize: "0.9rem",
                borderRadius: "4px",
                border: "1px solid #313131",
                backgroundColor: "#ccc",
                color: "black",
                cursor: "pointer",
                transition: "background-color 0.2s",
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.color = "white";
                e.currentTarget.style.backgroundColor = "#0056b3";
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.color = "black";
                e.currentTarget.style.backgroundColor = "#ccc";
              }}
            >
              Copy
            </button>
          </div>
          <div className="columns">{stringifyLayout}</div>
        </div>
      )}


      <ShowcaseLayout
        ref={showcaseLayoutRef}
        onLayoutChange={onLayoutChange}
        onBreakpointChange={setCurrentBreakpoint}
        slotContent={demoComponents}
        compactType={CONFIG.compactType}
        allStatic={CONFIG.allStatic}
        onCompactTypeChange={() => {}}
        onToggleStatic={() => {}}
        onNewLayout={() => {}}
        initialLayout={initialLayout}
        onMount={handleLayoutMount}
      />

    



      {/* Leverage Selection Modal */}
      <LeverageModal
        isOpen={isLeverageModalOpen}
        currentLeverage={tradeOperationRef.current?.leverage ?? 10}
        tempLeverage={tempLeverage}
        maxRatio={
          tradeOperationRef.current
            ? tradeOperationRef.current.calculateMaxRatio(tempLeverage)
            : tempLeverage.toFixed(2)
        }
        onLeverageChange={setTempLeverage}
        onConfirm={async () => {
          if (
            !selectedBucketId ||
            selectedBucketId === "--" ||
            selectedBucketId.trim() === ""
          ) {
            toast.error("Bucket Required", {
              description: "Please select a bucket before changing leverage.",
            });
            return;
          }
          if (tradeOperationRef.current) {
            setIsLeverageModalOpen(false);
            try {
              await tradeOperationRef.current.changeLeverage(tempLeverage, {
                bucketId: selectedBucketId,
              });
            } catch {
              if (tradeOperationRef.current) {
                setTempLeverage(tradeOperationRef.current.leverage);
              }
            }
          } else {
            setIsLeverageModalOpen(false);
          }
        }}
        onCancel={() => {
          if (tradeOperationRef.current) {
            setTempLeverage(tradeOperationRef.current.leverage);
          }
          setIsLeverageModalOpen(false);
        }}
      />

      <NumberPad
        isOpen={isNumberCalculatorOpen}
        value={numberCalculatorValue}
        min={0}
        max={numberCalculatorMax}
        onConfirm={(value) => {
          if (tradeOperationRef.current) {
            tradeOperationRef.current.setSizeInput(value);
          }
          setIsNumberCalculatorOpen(false);
        }}
        onCancel={() => setIsNumberCalculatorOpen(false)}
      />
    </div>
  );
}

export default function LayoutOnlyPage() {
  return (
    <SubscriptionProvider defaultWsURL={CONFIG.wsURL}>
      <LayoutOnlyContent />
    </SubscriptionProvider>
  );
}
